/**
 * sl-ddz8.7.2: Workspace Settings — AI Memory + Knowledge Graph Config
 *
 * Settings panel with toggles for:
 * - enablePersonalMemory (per-user AI memory)
 * - enableKnowledgeGraph (workspace-wide knowledge graph)
 *
 * Persists via GraphQL updateWorkspace mutation (same pattern as other workspace toggles).
 * Plan-gated: toggles disabled if the workspace plan does not include the feature.
 *
 * SOLID:
 * - S: Only AI memory/KG settings display and toggles
 * - D: Depends on useFeatureGate for plan checks, GraphQLService for mutations
 */

import { Button, ConfirmModal, Input, Switch } from '@affine/component';
import {
  SettingRow,
  SettingWrapper,
} from '@affine/component/setting-components';
import { GraphQLService } from '@affine/core/modules/cloud/services/graphql';
import { WorkspacePermissionService } from '@affine/core/modules/permissions';
import { useFeatureGate } from '@affine/core/modules/streamlain/hooks/use-feature-gate';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { EmbeddingService } from '@affine/core/modules/workspace-indexer-embedding';
import { useI18n } from '@affine/i18n';
import { useLiveData, useService } from '@toeverything/infra';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useState } from 'react';

import { SettingsCollapsibleSection } from '../shared/settings-collapsible';
import { requestWorkspaceSettingGraphQL } from '../shared/workspace-settings-api';

export const WorkspaceSettingAIMemory = ({
  onToggleChange,
  embeddingSettings,
  botsContent,
}: {
  onToggleChange?: (state: {
    personalMemory: boolean;
    knowledgeGraph: boolean;
  }) => void;
  embeddingSettings?: ReactNode;
  /** Expert Agents content rendered in bare mode inside this section */
  botsContent?: ReactNode;
}) => {
  const t = useI18n();
  const { isFeatureAllowed, getRequiredPlan, notifyFeatureLocked } =
    useFeatureGate();
  const graphqlService = useService(GraphQLService);
  const workspacePermissionService = useService(
    WorkspacePermissionService
  ).permission;
  const isOwner = useLiveData(workspacePermissionService.isOwner$);
  const isAdmin = useLiveData(workspacePermissionService.isAdmin$);
  const canEdit = Boolean(isOwner || isAdmin);
  const workspaceId = useService(WorkspaceService).workspace.id;

  // Reactive DA status — shared LiveData from EmbeddingService
  const embeddingService = useService(EmbeddingService);
  const docEmbeddingEnabled = useLiveData(
    embeddingService.embeddingEnabled.enabled$
  );

  const personalMemoryAllowed = isFeatureAllowed('ai-personal-memory');
  const knowledgeGraphAllowed = isFeatureAllowed('ai-knowledge-graph');
  const expertBotsAllowed = isFeatureAllowed('ai-expert-bots');

  const [personalMemoryEnabled, setPersonalMemoryEnabled] = useState(false);
  const [knowledgeGraphEnabled, setKnowledgeGraphEnabled] = useState(false);
  const [expertBotsEnabled, setExpertBotsEnabled] = useState(false);

  useEffect(() => {
    // Ensure DA status is fetched (may already be loaded by EmbeddingSettings)
    embeddingService.embeddingEnabled.getEnabled();

    let cancelled = false;
    graphqlService
      .gql({
        query: {
          id: 'workspaceMemorySettingsQuery',
          query: `query workspaceMemorySettings($id: String!) {
            workspace(id: $id) {
              enablePersonalMemory
              enableKnowledgeGraph
              enableExpertBots
            }
          }`,
          operationName: 'workspaceMemorySettings',
          definitionName: 'workspace',
        } as any,
        variables: { id: workspaceId } as any,
      })
      .then((res: any) => {
        if (!cancelled) {
          const pm = res?.workspace?.enablePersonalMemory ?? false;
          const kg = res?.workspace?.enableKnowledgeGraph ?? false;
          const eb = res?.workspace?.enableExpertBots ?? false;
          setPersonalMemoryEnabled(pm);
          setKnowledgeGraphEnabled(kg);
          setExpertBotsEnabled(eb);
          onToggleChange?.({ personalMemory: pm, knowledgeGraph: kg });
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [
    workspaceId,
    graphqlService,
    embeddingService.embeddingEnabled,
    onToggleChange,
  ]);

  const updateWorkspaceToggle = useCallback(
    (
      field:
        | 'enablePersonalMemory'
        | 'enableKnowledgeGraph'
        | 'enableExpertBots',
      value: boolean
    ) => {
      graphqlService
        .gql({
          query: {
            id: 'updateWorkspaceMemoryToggle',
            query: `mutation updateWorkspace($input: UpdateWorkspaceInput!) {
              updateWorkspace(input: $input) {
                id
                enablePersonalMemory
                enableKnowledgeGraph
                enableExpertBots
              }
            }`,
            operationName: 'updateWorkspace',
            definitionName: 'updateWorkspace',
          } as any,
          variables: {
            input: { id: workspaceId, [field]: value },
          } as any,
        })
        .catch(() => {
          if (field === 'enablePersonalMemory') {
            setPersonalMemoryEnabled(!value);
          } else if (field === 'enableKnowledgeGraph') {
            setKnowledgeGraphEnabled(!value);
          } else if (field === 'enableExpertBots') {
            setExpertBotsEnabled(!value);
          }
        });
    },
    [workspaceId, graphqlService]
  );

  // All memory features depend on Dokumenten-Analyse (enableDocEmbedding)
  // Backend PermissionTripleCheckService blocks everything when DA is off
  const blockedByDA = !docEmbeddingEnabled;

  const handleTogglePersonalMemory = useCallback(
    (checked: boolean) => {
      if (blockedByDA) return;
      if (!personalMemoryAllowed) {
        notifyFeatureLocked('ai-personal-memory');
        return;
      }
      if (!canEdit) return;
      setPersonalMemoryEnabled(checked);
      updateWorkspaceToggle('enablePersonalMemory', checked);
      onToggleChange?.({
        personalMemory: checked,
        knowledgeGraph: knowledgeGraphEnabled,
      });
    },
    [
      blockedByDA,
      personalMemoryAllowed,
      canEdit,
      notifyFeatureLocked,
      updateWorkspaceToggle,
      onToggleChange,
      knowledgeGraphEnabled,
    ]
  );

  const handleToggleKnowledgeGraph = useCallback(
    (checked: boolean) => {
      if (blockedByDA) return;
      if (!knowledgeGraphAllowed) {
        notifyFeatureLocked('ai-knowledge-graph');
        return;
      }
      if (!canEdit) return;
      setKnowledgeGraphEnabled(checked);
      updateWorkspaceToggle('enableKnowledgeGraph', checked);
      onToggleChange?.({
        personalMemory: personalMemoryEnabled,
        knowledgeGraph: checked,
      });
    },
    [
      blockedByDA,
      knowledgeGraphAllowed,
      canEdit,
      notifyFeatureLocked,
      updateWorkspaceToggle,
      onToggleChange,
      personalMemoryEnabled,
    ]
  );

  // Expert Agents require PM + KG to be enabled
  const expertBotsBlocked =
    blockedByDA || !personalMemoryEnabled || !knowledgeGraphEnabled;

  const handleToggleExpertBots = useCallback(
    (checked: boolean) => {
      if (expertBotsBlocked) return;
      if (!expertBotsAllowed) {
        notifyFeatureLocked('ai-expert-bots');
        return;
      }
      if (!canEdit) return;
      setExpertBotsEnabled(checked);
      updateWorkspaceToggle('enableExpertBots', checked);
    },
    [
      expertBotsBlocked,
      expertBotsAllowed,
      canEdit,
      notifyFeatureLocked,
      updateWorkspaceToggle,
    ]
  );

  const personalMemoryPlan = getRequiredPlan('ai-personal-memory');
  const descForToggle = (
    allowed: boolean,
    requiredPlan: string | null,
    activeDesc: string
  ) => {
    if (blockedByDA)
      return t[
        'com.streamlain.settings.workspace.ai-memory.doc-analysis-required'
      ]();
    if (!allowed)
      return t['com.streamlain.settings.workspace.ai-memory.plan-required']({
        plan: requiredPlan ?? 'Pro',
      });
    return activeDesc;
  };

  return (
    <SettingWrapper
      title={t['com.streamlain.settings.workspace.ai-memory.title']()}
    >
      {embeddingSettings}

      {/* Personal AI Memory — Toggle + Advanced */}
      <SettingRow
        name={t[
          'com.streamlain.settings.workspace.ai-memory.personal-memory-toggle'
        ]()}
        desc={descForToggle(
          personalMemoryAllowed,
          personalMemoryPlan,
          t['com.streamlain.settings.workspace.ai-memory.personal-desc']()
        )}
        style={{ marginBottom: 10 }}
      >
        <Switch
          data-testid="ai-memory-personal-toggle"
          checked={personalMemoryEnabled && !blockedByDA}
          disabled={!canEdit || !personalMemoryAllowed || blockedByDA}
          onChange={handleTogglePersonalMemory}
        />
      </SettingRow>
      {personalMemoryEnabled && isOwner && (
        <div style={{ marginBottom: 16 }}>
          <SettingsCollapsibleSection
            title={t['com.streamlain.settings.workspace.ai-memory.advanced']()}
            testId="personal-memory-advanced"
          >
            <SettingRow
              name={t[
                'com.streamlain.settings.workspace.ai-memory.personal-reset'
              ]()}
              desc={t[
                'com.streamlain.settings.workspace.ai-memory.personal-reset-desc'
              ]()}
            >
              <PersonalMemoryResetButton workspaceId={workspaceId} />
            </SettingRow>
          </SettingsCollapsibleSection>
        </div>
      )}

      {/* Knowledge Graph — hidden completely when the plan gate is closed */}
      {knowledgeGraphAllowed && (
        <>
          <SettingRow
            name={t[
              'com.streamlain.settings.workspace.ai-memory.knowledge-graph-toggle'
            ]()}
            desc={descForToggle(
              true,
              null,
              t['com.streamlain.settings.workspace.ai-memory.kg-desc']()
            )}
            style={{ marginBottom: 10 }}
          >
            <Switch
              data-testid="ai-memory-knowledge-graph-toggle"
              checked={knowledgeGraphEnabled && !blockedByDA}
              disabled={!canEdit || !knowledgeGraphAllowed || blockedByDA}
              onChange={handleToggleKnowledgeGraph}
            />
          </SettingRow>
          {knowledgeGraphEnabled && isOwner && (
            <div style={{ marginBottom: 16 }}>
              <SettingsCollapsibleSection
                title={t[
                  'com.streamlain.settings.workspace.ai-memory.advanced'
                ]()}
                testId="knowledge-graph-advanced"
              >
                <SettingRow
                  name={t[
                    'com.streamlain.settings.workspace.ai-memory.kg-reset'
                  ]()}
                  desc={t[
                    'com.streamlain.settings.workspace.ai-memory.kg-reset-desc'
                  ]()}
                >
                  <KgResetButton workspaceId={workspaceId} />
                </SettingRow>
              </SettingsCollapsibleSection>
            </div>
          )}
        </>
      )}

      {/* Expert Agents — Toggle + Advanced */}
      {expertBotsAllowed && botsContent && (
        <>
          <SettingRow
            name={t['com.streamlain.settings.workspace.bots.title']()}
            desc={
              expertBotsBlocked
                ? t[
                    'com.streamlain.settings.workspace.ai-memory.bots-blocked'
                  ]()
                : t['com.streamlain.settings.workspace.ai-memory.bots-desc']()
            }
            style={{ marginBottom: 10 }}
          >
            <Switch
              data-testid="ai-memory-expert-bots-toggle"
              checked={expertBotsEnabled && !expertBotsBlocked}
              disabled={!canEdit || !expertBotsAllowed || expertBotsBlocked}
              onChange={handleToggleExpertBots}
            />
          </SettingRow>
          {expertBotsEnabled && !expertBotsBlocked && (
            <div style={{ marginBottom: 16 }}>
              <SettingsCollapsibleSection
                title={t[
                  'com.streamlain.settings.workspace.ai-memory.advanced'
                ]()}
                testId="expert-agents-advanced"
              >
                {botsContent}
              </SettingsCollapsibleSection>
            </div>
          )}
        </>
      )}
    </SettingWrapper>
  );
};

// Reset Personal Memory with ConfirmModal
const PM_CONFIRM_WORD = 'RESET';

function PersonalMemoryResetButton({ workspaceId }: { workspaceId: string }) {
  const t = useI18n();
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [busy, setBusy] = useState(false);

  const handleReset = useCallback(async () => {
    setBusy(true);
    try {
      const res = await requestWorkspaceSettingGraphQL(
        workspaceId,
        `mutation ResetPM($workspaceId: String!) { resetPersonalMemories(workspaceId: $workspaceId) }`,
        { workspaceId },
        'foreground'
      );
      if (res.ok) {
        const json = await res.json();
        if (json.data?.resetPersonalMemories) {
          setOpen(false);
          setInputValue('');
        }
      }
    } finally {
      setBusy(false);
    }
  }, [workspaceId]);

  return (
    <>
      <Button
        variant="error"
        onClick={() => setOpen(true)}
        data-testid="ai-memory-reset-personal"
      >
        {t['com.streamlain.settings.workspace.ai-memory.reset']()}
      </Button>
      <ConfirmModal
        open={open}
        onOpenChange={v => {
          setOpen(v);
          if (!v) setInputValue('');
        }}
        title={t[
          'com.streamlain.settings.workspace.ai-memory.personal-reset'
        ]()}
        description={t[
          'com.streamlain.settings.workspace.ai-memory.personal-reset-confirm-desc'
        ]()}
        confirmText={
          busy
            ? t['com.streamlain.settings.workspace.ai-memory.resetting']()
            : t['com.streamlain.settings.workspace.ai-memory.reset']()
        }
        cancelText={t['com.streamlain.settings.workspace.ai-memory.cancel']()}
        confirmButtonOptions={{
          variant: 'error',
          disabled: inputValue.trim() !== PM_CONFIRM_WORD || busy,
          loading: busy,
        }}
        onConfirm={() => handleReset()}
      >
        <div style={{ fontSize: 13, marginBottom: 8 }}>
          {t['com.streamlain.settings.workspace.ai-memory.confirm-word']({
            word: PM_CONFIRM_WORD,
          })}
        </div>
        <Input
          type="text"
          value={inputValue}
          onChange={setInputValue}
          placeholder={PM_CONFIRM_WORD}
          data-testid="ai-memory-reset-personal-input"
        />
      </ConfirmModal>
    </>
  );
}

// sl-etat.4.2: Reset KG with ConfirmModal (same pattern as delete-account.tsx)
const CONFIRM_WORD = 'RESET';

function KgResetButton({ workspaceId }: { workspaceId: string }) {
  const t = useI18n();
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [busy, setBusy] = useState(false);

  const handleReset = useCallback(async () => {
    setBusy(true);
    try {
      const res = await requestWorkspaceSettingGraphQL(
        workspaceId,
        `mutation ResetKG($workspaceId: String!) { resetKnowledgeGraph(workspaceId: $workspaceId) }`,
        { workspaceId },
        'foreground'
      );
      if (res.ok) {
        const json = await res.json();
        if (json.data?.resetKnowledgeGraph) {
          setOpen(false);
          setInputValue('');
        }
      }
    } finally {
      setBusy(false);
    }
  }, [workspaceId]);

  return (
    <>
      <Button
        variant="error"
        onClick={() => setOpen(true)}
        data-testid="ai-memory-reset-kg"
      >
        {t['com.streamlain.settings.workspace.ai-memory.reset']()}
      </Button>
      <ConfirmModal
        open={open}
        onOpenChange={v => {
          setOpen(v);
          if (!v) setInputValue('');
        }}
        title={t['com.streamlain.settings.workspace.ai-memory.kg-reset']()}
        description={t[
          'com.streamlain.settings.workspace.ai-memory.kg-reset-confirm-desc'
        ]()}
        confirmText={
          busy
            ? t['com.streamlain.settings.workspace.ai-memory.resetting']()
            : t['com.streamlain.settings.workspace.ai-memory.reset']()
        }
        cancelText={t['com.streamlain.settings.workspace.ai-memory.cancel']()}
        confirmButtonOptions={{
          variant: 'error',
          disabled: inputValue.trim() !== CONFIRM_WORD || busy,
          loading: busy,
        }}
        onConfirm={() => handleReset()}
      >
        <div style={{ fontSize: 13, marginBottom: 8 }}>
          {t['com.streamlain.settings.workspace.ai-memory.confirm-word']({
            word: CONFIRM_WORD,
          })}
        </div>
        <Input
          type="text"
          value={inputValue}
          onChange={setInputValue}
          placeholder={CONFIRM_WORD}
          data-testid="ai-memory-reset-kg-input"
        />
      </ConfirmModal>
    </>
  );
}
